import React, { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import SEO from '../components/SEO';
import {
  FaStar,
  FaCheckCircle,
  FaGift,
  FaShieldAlt,
  FaClock,
  FaChartLine,
  FaArrowLeft,
  FaExternalLinkAlt,
} from 'react-icons/fa';

interface OfferData {
  id: string;
  name: string;
  bonus: string;
  image: string;
  rating: number;
  description: string;
  pros: string[];
  cons: string[];
  features: string[];
  minDeposit: string;
  withdrawalTime: string;
  license: string;
  url: string;
}

const OfferReview: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [selectedTab, setSelectedTab] = useState<'overview' | 'bonus' | 'features'>('overview');

  const offers: Record<string, OfferData> = {
    bwin: {
      id: 'bwin',
      name: 'Bwin',
      bonus: '100€',
      image: '/images/offers/bwin.webp',
      rating: 4.8,
      description:
        'Bwin est l\'un des leaders du marché français des paris sportifs. Avec une licence ANJ et une réputation solide, cette plateforme offre une expérience de paris complète avec des cotes compétitives et des bonus attractifs.',
      pros: [
        'Cotes parmi les plus compétitives du marché',
        'Interface utilisateur moderne et intuitive',
        'Large sélection de sports et de marchés',
        'Application mobile performante',
        'Support client réactif',
      ],
      cons: [
        'Bonus de bienvenue pourrait être plus élevé',
        'Quelques restrictions sur certains sports',
      ],
      features: [
        'Bonus de bienvenue jusqu\'à 100€',
        'Cashback régulier',
        'Pari combiné avec multiplicateur',
        'Streaming en direct',
        'Programme VIP',
      ],
      minDeposit: '10€',
      withdrawalTime: '24-48h',
      license: 'ANJ',
      url: '#',
    },
    unibet: {
      id: 'unibet',
      name: 'Unibet',
      bonus: '150€',
      image: '/images/offers/unibet.webp',
      rating: 4.9,
      description:
        'Unibet se distingue par son offre complète et ses promotions généreuses. La plateforme combine paris sportifs, casino et poker dans une expérience unifiée.',
      pros: [
        'Bonus de bienvenue très attractif',
        'Pari gratuit offert régulièrement',
        'Streaming live de qualité',
        'Application mobile excellente',
        'Retraits très rapides',
      ],
      cons: ['Interface parfois chargée', 'Cotes légèrement inférieures sur certains marchés'],
      features: [
        'Bonus de bienvenue 150€',
        'Pari gratuit chaque semaine',
        'Streaming en direct HD',
        'App mobile primée',
        'Retraits express',
      ],
      minDeposit: '10€',
      withdrawalTime: '12-24h',
      license: 'ANJ',
      url: '#',
    },
    vbet: {
      id: 'vbet',
      name: 'Vbet',
      bonus: '120€',
      image: '/images/offers/vbet.webp',
      rating: 4.7,
      description:
        'Vbet offre une expérience de paris moderne avec un accent sur l\'innovation et les technologies. La plateforme propose des fonctionnalités uniques et un programme VIP généreux.',
      pros: [
        'Interface très moderne',
        'Programme VIP avantageux',
        'Retraits rapides',
        'Cotes compétitives',
        'Support multilingue',
      ],
      cons: ['Moins de promotions que certains concurrents', 'App mobile en développement'],
      features: [
        'Bonus de bienvenue 120€',
        'Pari combiné avec boost',
        'Programme VIP exclusif',
        'Retraits rapides',
        'Cotes boostées quotidiennes',
      ],
      minDeposit: '10€',
      withdrawalTime: '24-48h',
      license: 'ANJ',
      url: '#',
    },
  };

  const offer = offers[id || ''] || offers.bwin;

  // Generate chart data
  const chartData = useMemo(() => {
    const months = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun'];
    return months.map((month, i) => ({
      month,
      value: 80 + Math.random() * 20,
    }));
  }, []);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Review',
    itemReviewed: {
      '@type': 'Organization',
      name: offer.name,
    },
    author: {
      '@type': 'Organization',
      name: 'BetSprinto',
    },
    reviewRating: {
      '@type': 'Rating',
      ratingValue: offer.rating,
      bestRating: 5,
    },
    reviewBody: offer.description,
  };

  return (
    <>
      <SEO
        title={`Avis ${offer.name} - Offre et Bonus ${offer.bonus}`}
        description={`Découvrez notre avis détaillé sur ${offer.name}. Bonus ${offer.bonus}, fonctionnalités, avantages et inconvénients. Note: ${offer.rating}/5.`}
        type="article"
        image={offer.image}
        jsonLd={jsonLd}
      />

      <div className="min-h-screen pt-28">
        {/* Breadcrumb */}
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center space-x-2 text-sm text-gray-400">
            <Link to="/" className="hover:text-accent-primary transition-colors">
              Accueil
            </Link>
            <span>/</span>
            <Link to="/comparaison" className="hover:text-accent-primary transition-colors">
              Comparaison
            </Link>
            <span>/</span>
            <span className="text-gray-300">{offer.name}</span>
          </div>
        </div>

        {/* Hero Section */}
        <section className="py-12 bg-dark-surface">
          <div className="container mx-auto px-4">
            <Link
              to="/comparaison"
              className="inline-flex items-center space-x-2 text-gray-400 hover:text-accent-primary mb-6 transition-colors"
            >
              <FaArrowLeft />
              <span>Retour à la comparaison</span>
            </Link>

            <div className="glass rounded-2xl p-8 md:p-12">
              <div className="flex flex-col md:flex-row items-start md:items-center gap-8">
                <div className="flex-shrink-0">
                  <div className="bg-dark-card p-6 rounded-xl">
                    <img
                      src={offer.image}
                      alt={offer.name}
                      className="w-48 h-auto object-contain"
                      loading="lazy"
                      width="192"
                      height="192"
                    />
                  </div>
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-4">
                    <h1 className="text-4xl md:text-5xl font-bold text-white">{offer.name}</h1>
                    <div className="flex items-center space-x-2 bg-accent-primary/20 px-4 py-2 rounded-full">
                      <FaStar className="text-yellow-400" size={20} />
                      <span className="text-white font-bold text-xl">{offer.rating}</span>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4 mb-6">
                    <div className="bg-accent-primary/20 text-accent-primary px-4 py-2 rounded-lg font-semibold">
                      Bonus: {offer.bonus}
                    </div>
                    <div className="text-gray-400">
                      <FaShieldAlt className="inline mr-2" />
                      {offer.license}
                    </div>
                  </div>
                  <p className="text-gray-300 text-lg mb-6">{offer.description}</p>
                  <a
                    href={offer.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center space-x-2 bg-accent-primary hover:bg-accent-primary/90 text-white font-semibold py-3 px-6 rounded-lg transition-all"
                  >
                    <span>Visiter le site</span>
                    <FaExternalLinkAlt />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Tabs */}
        <section className="py-8 border-b border-dark-border">
          <div className="container mx-auto px-4">
            <div className="flex space-x-4">
              {[
                { id: 'overview', label: 'Aperçu' },
                { id: 'bonus', label: 'Bonus' },
                { id: 'features', label: 'Fonctionnalités' },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setSelectedTab(tab.id as any)}
                  className={`px-6 py-3 rounded-lg font-semibold transition-all ${
                    selectedTab === tab.id
                      ? 'bg-accent-primary text-white'
                      : 'text-gray-400 hover:text-white hover:bg-dark-hover'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="py-12">
          <div className="container mx-auto px-4">
            {selectedTab === 'overview' && (
              <div className="grid md:grid-cols-2 gap-8">
                <div className="space-y-6">
                  <div className="glass p-6 rounded-xl">
                    <h2 className="text-2xl font-bold text-white mb-4">Avantages</h2>
                    <ul className="space-y-3">
                      {offer.pros.map((pro, i) => (
                        <li key={i} className="flex items-start space-x-3">
                          <FaCheckCircle className="text-accent-success flex-shrink-0 mt-1" />
                          <span className="text-gray-300">{pro}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
                <div className="space-y-6">
                  <div className="glass p-6 rounded-xl">
                    <h2 className="text-2xl font-bold text-white mb-4">Points à améliorer</h2>
                    <ul className="space-y-3">
                      {offer.cons.map((con, i) => (
                        <li key={i} className="flex items-start space-x-3">
                          <span className="text-red-400 flex-shrink-0 mt-1">•</span>
                          <span className="text-gray-300">{con}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {selectedTab === 'bonus' && (
              <div className="glass p-8 rounded-xl">
                <h2 className="text-3xl font-bold text-white mb-6">Détails du Bonus</h2>
                <div className="grid md:grid-cols-2 gap-6 mb-8">
                  <div className="bg-dark-card p-6 rounded-lg">
                    <FaGift className="text-accent-primary mb-4" size={32} />
                    <h3 className="text-xl font-bold text-white mb-2">Bonus de Bienvenue</h3>
                    <p className="text-3xl font-bold text-accent-primary mb-2">{offer.bonus}</p>
                    <p className="text-gray-400 text-sm">
                      Offert lors de votre premier dépôt éligible
                    </p>
                  </div>
                  <div className="bg-dark-card p-6 rounded-lg">
                    <FaClock className="text-accent-secondary mb-4" size={32} />
                    <h3 className="text-xl font-bold text-white mb-2">Délai d'Utilisation</h3>
                    <p className="text-3xl font-bold text-accent-secondary mb-2">30 jours</p>
                    <p className="text-gray-400 text-sm">
                      Pour utiliser votre bonus après activation
                    </p>
                  </div>
                </div>
                <div className="bg-dark-card p-6 rounded-lg">
                  <h3 className="text-xl font-bold text-white mb-4">Conditions</h3>
                  <ul className="space-y-2 text-gray-300">
                    <li>• Dépôt minimum: {offer.minDeposit}</li>
                    <li>• Pari minimum: 1.50€</li>
                    <li>• Rollover: 5x le montant du bonus</li>
                    <li>• Valable sur les paris simples et combinés</li>
                  </ul>
                </div>
              </div>
            )}

            {selectedTab === 'features' && (
              <div className="space-y-6">
                <div className="glass p-8 rounded-xl">
                  <h2 className="text-3xl font-bold text-white mb-6">Fonctionnalités Principales</h2>
                  <div className="grid md:grid-cols-2 gap-4">
                    {offer.features.map((feature, i) => (
                      <div key={i} className="flex items-center space-x-3 bg-dark-card p-4 rounded-lg">
                        <FaCheckCircle className="text-accent-success flex-shrink-0" />
                        <span className="text-gray-300">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="glass p-8 rounded-xl">
                  <h2 className="text-3xl font-bold text-white mb-6">Informations Pratiques</h2>
                  <div className="grid md:grid-cols-3 gap-6">
                    <div>
                      <div className="text-gray-400 text-sm mb-2">Dépôt minimum</div>
                      <div className="text-2xl font-bold text-white">{offer.minDeposit}</div>
                    </div>
                    <div>
                      <div className="text-gray-400 text-sm mb-2">Délai de retrait</div>
                      <div className="text-2xl font-bold text-white">{offer.withdrawalTime}</div>
                    </div>
                    <div>
                      <div className="text-gray-400 text-sm mb-2">Licence</div>
                      <div className="text-2xl font-bold text-white">{offer.license}</div>
                    </div>
                  </div>
                </div>

                {/* Chart */}
                <div className="glass p-8 rounded-xl">
                  <h2 className="text-3xl font-bold text-white mb-6">Évolution des Cotes</h2>
                  <div className="h-64 flex items-end justify-between gap-2">
                    {chartData.map((data, i) => (
                      <div key={i} className="flex-1 flex flex-col items-center">
                        <div
                          className="w-full bg-gradient-to-t from-accent-primary to-accent-secondary rounded-t-lg mb-2 transition-all hover:opacity-80"
                          style={{ height: `${(data.value / 100) * 200}px` }}
                        />
                        <span className="text-gray-400 text-xs">{data.month}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>
      </div>
    </>
  );
};

export default OfferReview;

